import React, { useState } from 'react';
import { Box, Typography, ButtonBase, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { InfoButton } from '../FormWidgets/Description';
import {
  channelKey,
  channelLabel,
  formatLightMinute,
  profileError,
  sortedPoints
} from '../Utils/light_profiles';
import { getLightProfileColour } from '../Utils/light_profile_colours';
import {
  resolveLightProfile,
  analyticLightProfile,
  lightOutputVertices
} from '../Utils/light_profile_source';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export default function LightProfilesOverview({
  controllerId,
  channels,
  sourceChannels = channels,
  subzones,
  cycle,
  zoneSettings,
  sunriseDuration = 0,
  compact = false,
  readOnly = false,
  nowMinute
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartWidth = mobile ? 480 : 800;
  const [hiddenProfiles, setHiddenProfiles] = useState({});
  const [frontProfiles, setFrontProfiles] = useState([]);
  const bringToFront = (keys) =>
    setFrontProfiles((current) => [...current.filter((key) => !keys.includes(key)), ...keys]);
  const drawRank = Object.fromEntries(frontProfiles.map((key, index) => [key, index]));
  const isHidden = (profile) => hiddenProfiles[channelKey(profile.channel)];
  const profiles = channels.map((channel) => {
    const subzone = subzones.find((s) => s.subzone === channel.subzone);
    const shared = Boolean(
      zoneSettings?.shared_toplight && channel.subzone === 0 && channel.channel === 0
    );
    const enabled = Boolean(
      (zoneSettings?.enabled ?? 1) &&
        (shared || (subzone?.enabled ?? 1)) &&
        subzone?.enabled_mask & (1 << channel.channel)
    );
    const inherited = resolveLightProfile(channel, sourceChannels, subzones, zoneSettings);
    const error = profileError(inherited.points, cycle);
    const curve =
      inherited.kind === 'analytic'
        ? analyticLightProfile(channel, cycle, sunriseDuration)
        : inherited.points;
    return {
      channel,
      group: shared ? -1 : channel.subzone,
      enabled,
      error,
      points: error ? [] : sortedPoints(curve, cycle),
      curve,
      inherited,
      subzone,
      shared,
      source: inherited.source,
      colour: getLightProfileColour(`${controllerId}:${channelKey(channel)}`),
      defaultMode: inherited.kind === 'off' ? 'lighting_off' : 'lighting_simple'
    };
  });
  // Fit all configured curves, not just currently visible curves, so eye toggles
  // never change their time alignment. Match the ordinary editor's Time padding.
  const pointMinutes = profiles.flatMap((profile) => profile.points.map((point) => point.minute));
  const start = Math.min(cycle.start - 60, ...pointMinutes);
  const end = Math.max(cycle.end + 60, ...(compact ? [cycle.start + 1380] : []), ...pointMinutes);
  const x = (minute) => 45 + ((minute - start) / (end - start)) * (chartWidth - 90);
  // Keep controller time on the same continuous day as the displayed curves.
  const now = nowMinute == null ? null :
    nowMinute + Math.ceil((start - nowMinute) / 1440) * 1440;
  const axisMaximum = channels.some((channel) => channel.maximum > 100) ? 115 : 100;
  const y = (level) => (185 - (level / axisMaximum) * 150).toFixed(3);
  const path = (profile) => {
    const { vertices } = lightOutputVertices({
      cycle,
      channel: profile.channel,
      enabled: profile.enabled && profile.inherited.kind !== 'off' && !profile.error,
      sunriseDuration,
      zoneMaster: zoneSettings?.master ?? 100,
      subzoneMaster: profile.shared ? 100 : profile.subzone?.master ?? 100,
      profilePoints: profile.inherited.kind === 'custom' ? profile.inherited.points : undefined,
      range: { start, end }
    });
    return vertices
      .map(([minute, level], index) => `${index ? 'L' : 'M'}${x(minute).toFixed(3)},${y(level)}`)
      .join(' ');
  };
  return (
    <Box my={compact ? 1 : 2}>
      <Typography variant="body2">
        {t('lighting_configured_output')}{' '}
        <InfoButton
          brief={t('lighting_overview_help')}
          content={t(readOnly ? 'lighting_info_chart_help' : 'lighting_overview_description')}
        />
      </Typography>
      <svg
        viewBox={`0 0 ${chartWidth} 230`}
        role="img"
        aria-label={t('lighting_overview_chart')}
        style={{ width: '100%', maxHeight: compact ? 230 : 320 }}
      >
        <rect
          x={x(cycle.start)}
          y={30}
          width={x(cycle.end) - x(cycle.start)}
          height={155}
          fill="currentColor"
          opacity={0.06}
        />
        {[0, 50, 100, ...(axisMaximum > 100 ? [115] : [])].map((level) => (
          <g key={level}>
            <line
              x1={45}
              x2={chartWidth - 45}
              y1={y(level)}
              y2={y(level)}
              stroke="currentColor"
              opacity={0.15}
            />
            <text x={0} y={Number(y(level)) + 4} fill="currentColor" fontSize={13}>
              {level}%
            </text>
          </g>
        ))}
        {profiles
          .filter((profile) => !isHidden(profile))
          .sort(
            (a, b) =>
              (drawRank[channelKey(a.channel)] ?? -1) - (drawRank[channelKey(b.channel)] ?? -1)
          )
          .map((profile) => (
            <path
              key={channelKey(profile.channel)}
              data-profile={channelKey(profile.channel)}
              d={path(profile)}
              fill="none"
              stroke={profile.colour}
              strokeWidth={3}
              strokeDasharray={profile.enabled ? undefined : '6 4'}
              opacity={profile.enabled ? 1 : 0.5}
            >
              <title>{channelLabel(profile.channel)}</title>
            </path>
          ))}
        {now !== null && now >= start && now <= end && (
          <g data-light-now={now}>
            <line x1={x(now)} x2={x(now)} y1={20} y2={190}
              stroke="currentColor" strokeWidth={2} strokeDasharray="4 3" />
            <text x={x(now)} y={15} fill="currentColor" textAnchor="middle" fontSize={13}>
              {t('lighting_info_now')}
            </text>
          </g>
        )}
        {[0, 1, 2, 3, 4].map((tick) => {
          const minute = Math.round(start + ((end - start) * tick) / 4);
          return (
            <text
              key={tick}
              x={x(minute)}
              y={218}
              fill="currentColor"
              textAnchor={tick === 0 ? 'start' : tick === 4 ? 'end' : 'middle'}
              fontSize={13}
            >
              {formatLightMinute(minute)}
            </text>
          );
        })}
      </svg>
      {!readOnly && <Box
        aria-label={t('lighting_overview_legend')}
        role="group"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}
      >
        {[...new Set(profiles.map((profile) => profile.group))]
          .sort((a, b) => a - b)
          .map((subzone) => {
            const name =
              subzone === -1
                ? t('lighting_shared_toplight')
                : `${t('lighting_subzone')} ${subzone + 1}`;
            const group = profiles.filter((profile) => profile.group === subzone);
            const hidden = group.every((profile) => isHidden(profile));
            return (
              <Box key={subzone} component="section" aria-label={name}>
                <ButtonBase
                  aria-label={t(hidden ? 'lighting_overview_show' : 'lighting_overview_hide', {
                    name
                  })}
                  aria-pressed={!hidden}
                  onClick={() => {
                    setHiddenProfiles((current) => ({
                      ...current,
                      ...Object.fromEntries(
                        group.map((profile) => [channelKey(profile.channel), !hidden])
                      )
                    }));
                    if (hidden) bringToFront(group.map((profile) => channelKey(profile.channel)));
                  }}
                  style={{ display: 'flex', gap: 8, minHeight: 44, padding: '0 8px' }}
                >
                  {hidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  <Typography
                    variant="subtitle2"
                    style={{ textDecoration: hidden ? 'line-through' : undefined }}
                  >
                    {name}
                  </Typography>
                </ButtonBase>
                <Box component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {profiles
                    .filter((profile) => profile.group === subzone)
                    .map((profile) => {
                      const key = channelKey(profile.channel);
                      const profileHidden = Boolean(isHidden(profile));
                      const name = channelLabel(profile.channel);
                      return (
                        <li key={key}>
                          <ButtonBase
                            aria-label={t(
                              profileHidden ? 'lighting_overview_show' : 'lighting_overview_hide',
                              { name }
                            )}
                            aria-pressed={!profileHidden}
                            onClick={() => {
                              setHiddenProfiles((current) => ({
                                ...current,
                                [key]: !current[key]
                              }));
                              if (profileHidden) bringToFront([key]);
                            }}
                            style={{
                              display: 'flex',
                              gap: 8,
                              minHeight: 44,
                              padding: '0 8px',
                              color: profile.colour
                            }}
                          >
                            {profileHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            <Typography
                              variant="body2"
                              style={{
                                textAlign: 'left',
                                textDecoration: profileHidden ? 'line-through' : undefined
                              }}
                            >
                              {name}
                              {!profile.enabled ? ` (${t('lighting_disabled')})` : ''}
                              {profile.error
                                ? ` (${t('lighting_invalid_profile')})`
                                : !profile.channel.points.length
                                  ? ` (${
                                      profile.source && profile.source !== profile.channel
                                        ? t('lighting_follows_main', {
                                            name: channelLabel(profile.source)
                                          })
                                        : t(profile.defaultMode)
                                    })`
                                  : ''}
                            </Typography>
                          </ButtonBase>
                        </li>
                      );
                    })}
                </Box>
              </Box>
            );
          })}
      </Box>}
    </Box>
  );
}
